import { FC, useCallback } from 'react';
import { LinearProgress, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import useDrawer from '@/hooks/use-drawer.hook';
import Icon from '@/components/icon';
import Text from '@/components/text';
import { EntityLink } from '../entity-link';
import { EntityLinkVariant } from '../entity-link/helpers';

interface Props {
  name: string;
  id?: string;
  confidence?: number;
  type?: 'intent' | 'regex';
  onChange?: () => unknown;
  variant?: EntityLinkVariant;
}

const useStyles = createUseStyles((theme: Theme) => ({
  confidence: {
    marginLeft: 'auto',
  },
  progress: {
    width: 100,
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.palette.surface.grey.main,
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.surface.successHighlight.main,
    },
  },
}));

const ClassifierLink: FC<Props> = ({
  name,
  id,
  confidence,
  type,
  onChange,
  variant = 'outlined',
}) => {
  const styles = useStyles();
  const { openDrawer } = useDrawer();

  const handleClick = useCallback(() => {
    if (!id) {
      return;
    }

    openDrawer('classifier', { id, onChange });
  }, [id, onChange, openDrawer]);

  if (!id) {
    return null;
  }

  const percentage = confidence ? Math.round(confidence * 100) : null;
  const showConfidence = !!percentage && type === 'intent';

  const IconEl = variant === 'outlined' ? <Icon name="classifier" color="grey" /> : undefined;

  const ConfidenceEl = showConfidence ? (
    <Stack direction="row" alignItems="center" gap={1} className={styles.confidence}>
      <LinearProgress
        value={percentage}
        className={styles.progress}
        variant="determinate"
        color="inherit"
      />
      <Text size="x-small" bold>
        {percentage}%
      </Text>
    </Stack>
  ) : null;

  return (
    <EntityLink
      title={name}
      image={IconEl}
      titleAdornment={ConfidenceEl}
      onClick={handleClick}
      variant={variant}
      size="medium"
    />
  );
};

export default ClassifierLink;
